'use client';

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { AppLayout } from '@/features/app/presentation/layouts/AppLayout';
import { APP_SECTION } from '@/features/app/constants/sections';
import type { AppSectionId } from '@/features/app/constants/sections';
import { ROUTES, appSectionPath, parseAppSectionFromPathname } from '@/app/routes';
import { useAuth } from '@/features/authentication/presentation/context/AuthContext';
import { useUser } from '@/features/app/user/presentation/context/UserContext';
import { useSubscription } from '@/features/app/subscription/presentation/context/SubscriptionContext';
import { useReminderSettings } from '@/features/app/reminder-settings/presentation/context/ReminderSettingsContext';
import {
  resolveAppShellReminderGateRedirect,
  subscriptionRequiresPlanChoice,
} from '@/features/authentication/presentation/composables/utils/session-navigation';

/**
 * Layout do app autenticado: shell estável + `<Outlet />` para cada subrota de secção
 * (evita remontar sidebar/header a cada troca de secção).
 */
export default function AppPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { state: authState, events: authEvents } = useAuth();
  const { state: userState, events: userEvents } = useUser();
  const { events: subscriptionEvents } = useSubscription();
  const { state: reminderState, events: reminderEvents } = useReminderSettings();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [logoutBusy, setLogoutBusy] = useState(false);
  const [reminderIntegrationGateActive, setReminderIntegrationGateActive] = useState(false);

  const currentSection = useMemo((): AppSectionId => {
    return parseAppSectionFromPathname(location.pathname) ?? APP_SECTION.DASHBOARD;
  }, [location.pathname]);

  const onSectionChange = useCallback(
    (section: AppSectionId) => {
      navigate(appSectionPath(section));
    },
    [navigate]
  );

  useEffect(() => {
    if (!authState.authReady) return;
    if (!authState.user?.uid) {
      setReminderIntegrationGateActive(false);
      navigate(ROUTES.LOGIN, { replace: true });
      return;
    }
    const uid = authState.user.uid;
    let cancelled = false;
    void (async () => {
      const domainUser = await userEvents.getUserById(uid);
      if (cancelled) return;
      if (!domainUser) {
        setReminderIntegrationGateActive(false);
        navigate(ROUTES.AUTH_ONBOARDING, { replace: true });
        return;
      }
      const subscription = await subscriptionEvents.getSubscriptionByUserId(uid);
      if (cancelled) return;
      if (subscriptionRequiresPlanChoice(subscription)) {
        setReminderIntegrationGateActive(false);
        navigate(ROUTES.CHOOSE_PLAN, { replace: true });
        return;
      }
      const settings = await reminderEvents.getByUserId();
      if (cancelled) return;
      const messageTestSended = settings?.message_test_sended === true;
      setReminderIntegrationGateActive(!messageTestSended);
      const gateRedirect = resolveAppShellReminderGateRedirect(location.pathname, messageTestSended);
      if (gateRedirect) {
        navigate(gateRedirect, { replace: true });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    authState.authReady,
    authState.user?.uid,
    location.pathname,
    navigate,
    /** Funções estáveis; `events` inteiro do reminder muda com `currentSettings` e rearmava o efeito em loop. */
    userEvents.getUserById,
    subscriptionEvents.getSubscriptionByUserId,
    reminderEvents.getByUserId,
    reminderState.currentSettings?.message_test_sended,
  ]);

  const handleLogout = useCallback(async () => {
    setLogoutBusy(true);
    try {
      await authEvents.logout();
      navigate(ROUTES.LOGIN, { replace: true });
    } finally {
      setLogoutBusy(false);
    }
  }, [authEvents, navigate]);

  return (
    <AppLayout
      currentSection={currentSection}
      onSectionChange={onSectionChange}
      sidebarOpen={sidebarOpen}
      onSidebarOpenChange={setSidebarOpen}
      onLogout={handleLogout}
      isLoggingOut={logoutBusy || authState.loading}
      companyName={userState.currentUser?.company_name ?? undefined}
      reminderIntegrationGateActive={reminderIntegrationGateActive}
    >
      <Outlet />
    </AppLayout>
  );
}
